import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import SiteMap, { SITE_LABELS } from "@/components/injection/SiteMap";
import { getHistory } from "@/lib/injectionApi";
import { C } from "@/lib/brand";

// Member injection history. Two views:
//   · TIMELINE — reverse-chronological entries with site, dose, notes
//   · ROTATION — interactive SiteMap with usage heatmap (last 14d)
//
// Empty state is intentionally generous — first injections are big
// emotional moments and we want the page to greet, not nag.
export default function InjectionHistory() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState(null);
  const [view, setView] = useState("timeline");

  // BE is authoritative — on failure we render the empty state rather
  // than fragmenting data per-device with a localStorage fallback.
  const load = async () => {
    try {
      const r = await getHistory({ limit: 50 });
      setEntries(r.entries || []);
    } catch {
      setEntries([]);
    }
  };
  useEffect(() => {
    load();
    // Re-pull whenever the tab regains focus so a log made on phone
    // shows up on desktop without a sync button.
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  // Rotation heatmap — counts of last 14 days.
  const heatmap = useMemo(() => {
    if (!entries) return {};
    const cutoff = Date.now() - 14 * 86400000;
    const m = {};
    entries.forEach((e) => {
      if (!e.site) return;
      if (new Date(e.loggedAt).getTime() < cutoff) return;
      m[e.site] = (m[e.site] || 0) + 1;
    });
    return m;
  }, [entries]);

  const streak = useMemo(() => {
    if (!entries || entries.length === 0) return 0;
    // Count consecutive days back from today.
    const days = new Set(
      entries.map((e) => new Date(e.loggedAt).toDateString())
    );
    let count = 0;
    const d = new Date();
    while (days.has(d.toDateString())) {
      count++;
      d.setDate(d.getDate() - 1);
    }
    return count;
  }, [entries]);

  if (entries === null) {
    return (
      <div style={{ minHeight: "100vh", background: C.cream, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div data-testid="injection-history" style={{ minHeight: "100vh", background: C.cream, color: C.ink }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: `1px solid ${C.warmGray3}`, background: C.cream, position: "sticky", top: 0, zIndex: 30 }}>
        <Logo size={20} />
        <button
          type="button"
          onClick={() => navigate("/app")}
          data-testid="history-back"
          style={{ background: "transparent", border: 0, color: C.warmGray, fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", cursor: "pointer" }}
        >
          ← DASHBOARD
        </button>
      </header>

      <section style={{ maxWidth: 880, margin: "0 auto", padding: "32px 24px 16px" }}>
        <Eyebrow color={C.peachDeep}>★ INJECTION HISTORY</Eyebrow>
        <Headline before="EVERY DOSE," italic="every site." size={44} className="mt-4" />

        {/* Stats strip */}
        <div
          data-testid="history-stats"
          style={{
            marginTop: 28,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 14,
          }}
        >
          <StatCard label="TOTAL LOGGED" value={String(entries.length)} />
          <StatCard label="LAST 14 DAYS" value={String(Object.values(heatmap).reduce((a, b) => a + b, 0))} />
          <StatCard label="STREAK" value={`${streak}d`} accent />
          <StatCard label="MOST USED" value={mostUsedLabel(heatmap)} subtle />
        </div>

        <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <nav
            data-testid="history-view-toggle"
            style={{
              display: "inline-flex",
              border: `1px solid ${C.ink}`,
              background: C.pure,
            }}
          >
            {[
              { id: "timeline", label: "TIMELINE" },
              { id: "rotation", label: "ROTATION" },
            ].map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setView(v.id)}
                data-testid={`history-view-${v.id}`}
                style={{
                  padding: "10px 18px",
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: "0.22em",
                  border: 0,
                  background: view === v.id ? C.ink : "transparent",
                  color: view === v.id ? C.cream : C.ink,
                  cursor: "pointer",
                }}
              >
                {v.label}
              </button>
            ))}
          </nav>
          <Button variant="peach" size="md" testId="history-log-cta" onClick={() => navigate("/app/injection/log")}>
            + LOG INJECTION
          </Button>
        </div>
      </section>

      <main style={{ maxWidth: 880, margin: "0 auto", padding: "20px 24px 80px" }}>
        {entries.length === 0 ? (
          <EmptyState onLog={() => navigate("/app/injection/log")} />
        ) : view === "timeline" ? (
          <Timeline entries={entries} />
        ) : (
          <div
            data-testid="history-rotation"
            style={{ marginTop: 12, background: C.pure, border: `1px solid ${C.warmGray3}`, padding: 28 }}
          >
            <p style={{ fontSize: 14, color: C.warmGray, lineHeight: 1.65, maxWidth: 520, marginBottom: 22 }}>
              Numbers show how many times you've injected each site in the last 14 days. Pick from the dimmest spots next.
            </p>
            <SiteMap heatmap={heatmap} readOnly testId="history-site-map" />
          </div>
        )}
      </main>
    </div>
  );
}

function Timeline({ entries }) {
  // Group by day for editorial breaks.
  const groups = [];
  let current = null;
  entries.forEach((e) => {
    const dayKey = new Date(e.loggedAt).toDateString();
    if (!current || current.day !== dayKey) {
      current = { day: dayKey, items: [] };
      groups.push(current);
    }
    current.items.push(e);
  });

  return (
    <div data-testid="history-timeline" style={{ display: "flex", flexDirection: "column", gap: 28, marginTop: 12 }}>
      {groups.map((g) => (
        <section key={g.day}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.22em",
              color: C.peachDeep,
              marginBottom: 10,
            }}
          >
            ★ {formatDay(g.day)}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {g.items.map((e) => <Entry key={e.id} e={e} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function Entry({ e }) {
  return (
    <article
      data-testid={`history-entry-${e.id}`}
      style={{
        background: C.pure,
        border: `1px solid ${C.warmGray3}`,
        padding: 18,
        display: "flex",
        gap: 16,
        alignItems: "flex-start",
      }}
    >
      <div
        aria-hidden
        style={{
          width: 8,
          alignSelf: "stretch",
          background: e.difficulty >= 4 ? C.coral : e.difficulty >= 3 ? C.peachDeep : C.sage,
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.ink, letterSpacing: "-0.01em" }}>
            {e.compound || "Injection"} {e.doseMg != null && <span style={{ color: C.warmGray, fontWeight: 600 }}>· {e.doseMg}mg</span>}
          </div>
          <time style={{ fontSize: 12, color: C.warmGray, fontWeight: 600 }}>
            {new Date(e.loggedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
          </time>
        </div>
        <div style={{ marginTop: 4, fontSize: 13, color: C.warmGray }}>
          {SITE_LABELS[e.site] || e.site || "Site not recorded"}
        </div>
        {e.notes && (
          <p style={{ marginTop: 8, fontSize: 13, color: C.ink, lineHeight: 1.55, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            "{e.notes}"
          </p>
        )}
      </div>
    </article>
  );
}

function StatCard({ label, value, accent, subtle }) {
  return (
    <div
      style={{
        background: accent ? C.ink : C.pure,
        color: accent ? C.cream : C.ink,
        border: `1px solid ${accent ? C.ink : C.warmGray3}`,
        padding: 18,
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: accent ? C.peach : C.warmGray }}>
        {label}
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: subtle ? 16 : 26,
          fontWeight: 800,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function EmptyState({ onLog }) {
  return (
    <div
      data-testid="history-empty"
      style={{
        marginTop: 28,
        padding: 36,
        background: C.pure,
        border: `1px dashed ${C.warmGray3}`,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
        ★ NO INJECTIONS LOGGED YET
      </div>
      <h2 style={{ marginTop: 14, fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em", color: C.ink }}>
        Your history starts <span style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontWeight: 400 }}>here.</span>
      </h2>
      <p style={{ marginTop: 12, fontSize: 14, color: C.warmGray, lineHeight: 1.65, maxWidth: 440, margin: "12px auto 0" }}>
        Log your first injection — site, dose, how it felt. Three taps. We'll start building your rotation map automatically.
      </p>
      <div style={{ marginTop: 24 }}>
        <Button variant="primary" size="lg" testId="history-empty-cta" onClick={onLog}>
          LOG MY FIRST INJECTION →
        </Button>
      </div>
    </div>
  );
}

function formatDay(dayStr) {
  const d = new Date(dayStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return "TODAY";
  if (diffDays === 1) return "YESTERDAY";
  if (diffDays < 7) return d.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase();
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric" }).toUpperCase();
}

function mostUsedLabel(heatmap) {
  const entries = Object.entries(heatmap);
  if (entries.length === 0) return "—";
  const [slug] = entries.sort((a, b) => b[1] - a[1])[0];
  return SITE_LABELS[slug]?.split(" · ")[0] || slug;
}
